import React, { useMemo, useState } from 'react';
import { Combobox, Group, Pill, PillsInput, useCombobox } from '@mantine/core';
import { t } from 'i18next';
import { IHealthcareProfessional } from '../../../global/entities/healthcareProfessional/HealthcareProfessional';

export type IHcpMultiSelectProps = {
    value?: number[];
    onChange: (values: number[]) => void;
    isError?: boolean;
    hcpsData?: IHealthcareProfessional[];
    placeholder?: string;
};

const HcpMultiSelect: React.FC<IHcpMultiSelectProps> = ({
    value = [],
    onChange,
    isError = false,
    hcpsData = [],
    placeholder,
}) => {
    const [search, setSearch] = useState('');

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    });

    const handleToggleHcp = (hcpId: number) => {
        if (value.includes(hcpId)) onChange(value.filter((id) => id !== hcpId));
        else onChange([...value, hcpId]);
    };

    const handleValueRemove = (val: string) => {
        onChange(value.filter((id) => id !== parseInt(val)));
    };

    const labelFor = (hcp: IHealthcareProfessional) => {
        const composed = `${hcp.firstName ?? ''} ${hcp.lastName ?? ''}`.trim();
        return composed.length > 0 ? composed : hcp.name;
    };

    const filteredOptions = useMemo(() => {
        const needle = search.toLowerCase();
        return hcpsData.filter((hcp) => labelFor(hcp).toLowerCase().includes(needle));
    }, [hcpsData, search]);

    const pills = value
        .map((selectedId) => {
            const hcp = hcpsData.find((h) => h.id === selectedId);
            if (!hcp) return null;
            return (
                <Pill
                    key={selectedId}
                    withRemoveButton
                    onRemove={() => handleValueRemove(selectedId.toString())}
                >
                    {labelFor(hcp)}
                </Pill>
            );
        })
        .filter(Boolean);

    const options = filteredOptions.map((hcp) => {
        const isSelected = value.includes(hcp.id);
        return (
            <Combobox.Option value={hcp.id.toString()} key={hcp.id}>
                <Group gap="sm">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        style={{ pointerEvents: 'none' }}
                    />
                    <span>{labelFor(hcp)}</span>
                    {hcp.healthcareProfessionalType && (
                        <span style={{ fontSize: '0.8em', color: 'gray' }}>
                            {hcp.healthcareProfessionalType}
                        </span>
                    )}
                </Group>
            </Combobox.Option>
        );
    });

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={(val) => {
                handleToggleHcp(parseInt(val));
                setSearch('');
            }}
        >
            <Combobox.DropdownTarget>
                <PillsInput onClick={() => combobox.openDropdown()} error={isError}>
                    <Pill.Group>
                        {pills}
                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                placeholder={placeholder}
                                value={search}
                                onChange={(event) => {
                                    combobox.updateSelectedOptionIndex();
                                    setSearch(event.currentTarget.value);
                                }}
                                onFocus={() => combobox.openDropdown()}
                                onBlur={() => combobox.closeDropdown()}
                                onKeyDown={(event) => {
                                    if (event.key === 'Backspace' && search.length === 0) {
                                        event.preventDefault();
                                        if (value.length > 0)
                                            handleValueRemove(value[value.length - 1].toString());
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {options.length > 0 ? (
                        options
                    ) : (
                        <Combobox.Empty>{t('eicf.admin.noHcpsFound')}</Combobox.Empty>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};

export default HcpMultiSelect;
